// src/pages/Settings.js — ADDED: Dispatch event after settings change
import React, { useState, useEffect, useRef } from 'react';
import { get, post, patch } from '../utils/api';

const STAGE_OPTIONS = [
  { value: 1, label: '1 - ERP/Initial', description: 'Invoices start at initial import stage' },
  { value: 2, label: '2 - Stock Removal', description: 'Invoices jump directly to stock removal' },
  { value: 3, label: '3 - Checking', description: 'Invoices jump directly to checking' },
  { value: 4, label: '4 - Ready for Delivery', description: 'Invoices jump directly to Ready for Delivery (auto-completes immediately)' },
];

// Separate, wider list for the manual override picker — includes stages
// that don't make sense as a NEW invoice's starting point (Dispatched,
// Delivered, Complete), but are valid override targets for an existing
// invoice.
const OVERRIDE_STAGE_OPTIONS = [
  { value: '1', label: '1 - ERP/Initial' },
  { value: '2', label: '2 - Stock Removal' },
  { value: '3', label: '3 - Checking' },
  { value: '4', label: '4 - Ready for Delivery' },
  { value: '5', label: '5 - Dispatched' },
  { value: '6', label: '6 - Delivered' },
  { value: 'complete', label: '✅ Complete' },
];

export default function Settings() {
  const user = JSON.parse(localStorage.getItem('dms_user') || '{}');
  const [settings, setSettings] = useState([]);
  const [initialStage, setInitialStage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [invoiceNumberToOverride, setInvoiceNumberToOverride] = useState('');
  const [stageToOverride, setStageToOverride] = useState('2');
  const [overridingInvoice, setOverridingInvoice] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState('');
  const invoiceInputRef = useRef(null);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await get('/api/settings');
        setSettings(response.settings || []);
        
        // Get current initial stage
        const stageResponse = await get('/api/settings/invoice-flow');
        if (stageResponse && stageResponse.initial_stage) {
          setInitialStage(stageResponse.initial_stage);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        setMessage('Error loading settings');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // ADDED: Helper function to dispatch settings change event across tabs
  const notifySettingsChanged = () => {
    // Use localStorage to broadcast across tabs
    localStorage.setItem('dms-settings-changed', Date.now().toString());
    // Also dispatch local event for same tab
    window.dispatchEvent(new CustomEvent('dms-settings-changed', { detail: { timestamp: Date.now() } }));
  };

  // Update initial stage for new invoices
  const handleUpdateInvoiceFlow = async () => {
    if (user.role !== 'owner') {
      setMessage('Only owners can change invoice flow');
      return;
    }

    setSaving(true);
    try {
      const response = await post('/api/settings/invoice-flow', { stage: initialStage });
      setMessage(`✅ ${response.message}`);
      
      // ADDED: Notify other pages about the change
      notifySettingsChanged();
      
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      console.error('Error updating invoice flow:', err);
      setMessage(`❌ Error: ${err.message || 'Failed to save'}`);
    } finally {
      setSaving(false);
    }
  };

  // Manual override invoice stage
  const handleOverrideInvoice = async () => {
    if (user.role !== 'owner') {
      setOverrideMessage('Only owners can override invoice stages');
      return;
    }

    if (!invoiceNumberToOverride.trim()) {
      setOverrideMessage('Please enter an invoice number');
      return;
    }

    setOverridingInvoice(true);
    try {
      // First, find the invoice ID by invoice number
      const url = `/api/invoices?invoice_number=${encodeURIComponent(invoiceNumberToOverride)}`;
      const invoicesResponse = await get(url);

      if (!invoicesResponse.invoices || invoicesResponse.invoices.length === 0) {
        setOverrideMessage('❌ Invoice not found');
        setOverridingInvoice(false);
        setInvoiceNumberToOverride('');
        if (invoiceInputRef.current) invoiceInputRef.current.focus();
        return;
      }

      const invoice = invoicesResponse.invoices[0];
      // Use invoice_id (not id) from the response
      const invoiceId = invoice.invoice_id;
      const invoiceNumber = invoice.invoice_number;

      if (!invoiceId) {
        setOverrideMessage('❌ Invalid invoice data - missing ID');
        setOverridingInvoice(false);
        return;
      }

      // Map stage selection to status string — stageToOverride is now a
      // string (numbers 1-6, or literally "complete") since HTML <select>
      // values are always strings and "complete" isn't a number.
      const stageMap = {
        1: 'stage_1_erp',
        2: 'stage_2_stock_removal',
        3: 'stage_3_checking',
        4: 'stage_4_packing',
        5: 'stage_5_dispatched',
        6: 'stage_6_delivered',
        complete: 'complete',
      };

      const newStatus = stageMap[stageToOverride];
      if (!newStatus) {
        setOverrideMessage('❌ Invalid stage selected');
        setOverridingInvoice(false);
        return;
      }

      console.log(`Updating invoice ${invoiceNumber} (ID: ${invoiceId}) to status: ${newStatus}`);

      // Update invoice status directly
      const response = await patch(`/api/invoices/${invoiceId}`, { status: newStatus });
      setOverrideMessage(`✅ Invoice moved to ${newStatus.replace(/_/g, ' ')}`);
      setInvoiceNumberToOverride('');
      setStageToOverride('2');

      // ADDED: Notify other pages about the change
      notifySettingsChanged();

      setTimeout(() => setOverrideMessage(''), 5000);
      // Refocus so a barcode scanner (or the person) can immediately scan
      // or type the next invoice number without clicking back into the field.
      if (invoiceInputRef.current) invoiceInputRef.current.focus();
    } catch (err) {
      console.error('Error overriding invoice:', err);
      setOverrideMessage(`❌ Error: ${err.message || 'Failed to override'}`);
    } finally {
      setOverridingInvoice(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  // Only show invoice flow controls if user is owner
  const isOwner = user.role === 'owner';

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Settings</h1>
        <p className="text-gray-600">Manage system configuration and invoice flow</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Invoice Flow Configuration - Owner Only */}
      {isOwner && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4 text-blue-900">📋 Invoice Flow Configuration</h2>
          <p className="text-gray-700 mb-4">
            Choose which stage all new invoices (from Marg ERP) should start at. 
            {initialStage > 1 && ` Currently: Stage ${initialStage}`}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {STAGE_OPTIONS.map(option => (
              <button
                key={option.value}
                onClick={() => setInitialStage(option.value)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  initialStage === option.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 bg-white hover:border-blue-400'
                }`}
              >
                <div className="font-bold text-lg">{option.label}</div>
                <div className="text-sm text-gray-600">{option.description}</div>
              </button>
            ))}
          </div>

          <button
            onClick={handleUpdateInvoiceFlow}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-semibold transition-colors"
          >
            {saving ? 'Saving...' : '✓ Save Invoice Flow Setting'}
          </button>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>⚠️ Note:</strong> This setting applies to all new invoices synced from Marg ERP going forward.
              {initialStage > 1 && ` Invoices will skip stages 1 to ${initialStage - 1} and appear directly in stage ${initialStage}.`}
              Dashboard and Heatmap will automatically refresh after you save.
            </p>
          </div>
        </div>
      )}

      {/* Manual Invoice Stage Override - Owner Only */}
      {isOwner && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4 text-blue-900">🎯 Manual Invoice Override</h2>
          <p className="text-gray-700 mb-4">
            Override the current stage of any invoice manually. This creates a new stage entry for the invoice.
          </p>

          {overrideMessage && (
            <div className={`p-4 rounded-lg mb-4 ${overrideMessage.startsWith('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {overrideMessage}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Invoice Number <span className="text-xs font-normal text-gray-400">(type or scan barcode)</span>
              </label>
              <input
                ref={invoiceInputRef}
                type="text"
                placeholder="e.g., MNG13456"
                value={invoiceNumberToOverride}
                onChange={(e) => setInvoiceNumberToOverride(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && invoiceNumberToOverride.trim() && !overridingInvoice) handleOverrideInvoice(); }}
                autoComplete="off"
                className="w-full px-4 py-2 border-2 border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Move to Stage</label>
              <select
                value={stageToOverride}
                onChange={(e) => setStageToOverride(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {OVERRIDE_STAGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleOverrideInvoice}
                disabled={overridingInvoice || !invoiceNumberToOverride.trim()}
                className="w-full px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 font-semibold transition-colors"
              >
                {overridingInvoice ? 'Processing...' : '→ Override Stage'}
              </button>
            </div>
          </div>

          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-800">
              <strong>⚠️ Important:</strong> This updates the invoice status and moves it to the selected stage.
              The invoice will immediately appear in the selected stage's queue. Dashboard and Heatmap will automatically refresh.
              Use with caution!
            </p>
          </div>
        </div>
      )}

      {/* Non-Owner Message */}
      {!isOwner && (
        <div className="bg-yellow-50 rounded-lg shadow-lg p-6 border border-yellow-200">
          <h2 className="text-2xl font-bold mb-2 text-yellow-900">⚠️ Owner Access Required</h2>
          <p className="text-yellow-800">
            Invoice Flow Configuration and Manual Override features are only available to owners.
            Your current role is: <strong>{user.role || 'unknown'}</strong>
          </p>
        </div>
      )}

      {/* System Settings Table */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4 text-blue-900">⚙️ System Settings</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b-2 border-gray-300">
              <tr>
                <th className="px-4 py-3 text-left">Setting Key</th>
                <th className="px-4 py-3 text-left">Value</th>
                <th className="px-4 py-3 text-left">Description</th>
              </tr>
            </thead>
            <tbody>
              {settings.map((setting, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 font-mono text-xs">{setting.key}</td>
                  <td className="px-4 py-3">
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">{setting.value}</code>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{setting.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
